/**
 * Token Encoding Utilities for Component Mask Detection
 *
 * Provides obfuscation for canary tokens and source URLs in detection URLs.
 * Encoded tokens look like content hashes (e.g., webpack chunk hashes).
 */

import {
  TOKEN_ENCODING_PREFIX,
  DECOY_VERSIONS,
  DETECTION_PARAMS,
} from '../config/constants'

// ============================================================================
// Token Encoding/Decoding
// ============================================================================

/**
 * Encode a canary token for URL embedding
 *
 * Uses base64url encoding with a prefix to make it look like a content hash.
 * Example: "abc123" -> "wYWJjMTIz"
 *
 * @param token - The raw canary token
 * @returns Encoded token string
 */
export function encodeToken(token: string): string {
  const base64 = btoa(token)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
  return TOKEN_ENCODING_PREFIX + base64
}

/**
 * Decode an encoded token back to original
 *
 * @param encoded - The encoded token string
 * @returns Original token, or null if invalid/malformed
 */
export function decodeToken(encoded: string): string | null {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- defensive: callers may pass null at runtime
  if (!encoded?.startsWith(TOKEN_ENCODING_PREFIX)) {
    return null
  }

  try {
    const base64 = encoded
      .slice(TOKEN_ENCODING_PREFIX.length)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    // Add padding if needed
    const paddingNeeded = (4 - (base64.length % 4)) % 4
    const padded = base64 + '='.repeat(paddingNeeded)
    return atob(padded)
  } catch {
    return null
  }
}

// ============================================================================
// Source URL Encoding/Decoding
// ============================================================================

/**
 * Encode a source URL for the 'r' parameter (JS embed)
 *
 * @param url - The source URL to encode
 * @returns Base64url encoded string
 */
export function encodeSourceUrl(url: string): string {
  return btoa(url)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

/**
 * Decode a source URL from the 'r' parameter
 *
 * @param encoded - The encoded source URL
 * @returns Original URL, or null if invalid
 */
export function decodeSourceUrl(encoded: string): string | null {
  if (!encoded) {
    return null
  }

  try {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const paddingNeeded = (4 - (base64.length % 4)) % 4
    const padded = base64 + '='.repeat(paddingNeeded)
    return atob(padded)
  } catch {
    return null
  }
}

// ============================================================================
// URL Building Utilities
// ============================================================================

/**
 * Generate a fake cache-buster value
 *
 * @returns Random alphanumeric string
 */
export function generateCacheBuster(): string {
  return Math.random().toString(36).substring(2, 10)
}

/**
 * Select a decoy version deterministically based on token
 *
 * Ensures the same token always gets the same version for consistency.
 *
 * @param token - The canary token
 * @returns A decoy version string
 */
export function selectDecoyVersion(token: string): string {
  const hash = simpleHash(token)
  // DECOY_VERSIONS is a non-empty const array defined in constants.ts
  // Using non-null assertion since we know the array has entries
  const index = hash % DECOY_VERSIONS.length
  const version: string | undefined = DECOY_VERSIONS[index]
  return version ?? '1.0.0'
}

/**
 * Simple string hash for deterministic selection
 */
function simpleHash(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

/**
 * Options for building a detection URL
 */
export type DetectionUrlOptions = {
  /** Base URL for the detection service (e.g., "https://d.spoofio.com") */
  baseUrl: string
  /** Asset path (e.g., "/assets/widget.css") */
  path: string
  /** Raw canary token */
  token: string
}

/**
 * Build a complete detection URL with obfuscation
 *
 * Creates a URL that looks like a versioned CDN asset:
 * /assets/widget.css?v=2.4.0&h=wYWJjMTIz&cb=k8f3m2x1
 *
 * @param options - URL building options
 * @returns Complete detection URL
 */
export function buildDetectionUrl(options: DetectionUrlOptions): string {
  const { baseUrl, path, token } = options
  const url = new URL(path, baseUrl)

  // Add decoy version first (looks like primary param)
  url.searchParams.set(DETECTION_PARAMS.VERSION, selectDecoyVersion(token))

  // Add encoded token
  url.searchParams.set(DETECTION_PARAMS.TOKEN, encodeToken(token))

  // Add cache buster
  url.searchParams.set(DETECTION_PARAMS.CACHE_BUST, generateCacheBuster())

  return url.toString()
}

/**
 * Build a detection URL for JS embed with source capture
 *
 * The returned URL includes a placeholder for the source URL that should
 * be replaced with actual location.href at runtime.
 *
 * @param options - URL building options
 * @returns Detection URL with source capture placeholder
 */
export function buildDetectionUrlWithSourceCapture(
  options: DetectionUrlOptions
): string {
  const baseUrl = buildDetectionUrl(options)
  // Add placeholder for source - will be replaced by JS at runtime
  return `${baseUrl}&${DETECTION_PARAMS.SOURCE}=`
}

/**
 * Build a bootstrap script URL for CSP-friendly external script embeds.
 *
 * Returns a .js URL with `b=1` parameter. The detection worker serves a small
 * script that captures `location.href` and creates a `<link>` element for
 * precise source URL detection. Customers add this as an external `<script src>`,
 * avoiding the need for `'unsafe-inline'` in their CSP.
 *
 * CSP requirement: `script-src https://cdn.stylerender.net` (or staging domain)
 *
 * @param options - URL building options (path should end in .js)
 * @returns Bootstrap script URL with boot flag
 */
export function buildBootstrapScriptUrl(
  options: DetectionUrlOptions
): string {
  const baseUrl = buildDetectionUrl(options)
  return `${baseUrl}&${DETECTION_PARAMS.BOOT}=1`
}
